import { useEffect, useState } from 'react'
import { Bot, Plus, Trash2, Save, Brain } from 'lucide-react'
import { api } from '@/lib/api'

interface BotProfile {
  id: string
  name: string
  purpose: string | null
  kind: 'CHAT' | 'VOICE'
  mode: 'OFF' | 'SHADOW' | 'SUGGESTIVE' | 'AUTOPILOT'
  systemPrompt: string
  channels: string[]
  knowledgeBaseId: string | null
  model: string
  confidenceThreshold: number | string
  responseDelaySeconds: number
  fallbackKeywords: string[]
  active: boolean
  knowledgeBase: { id: string; name: string; _count: { sources: number } } | null
  _count?: { faqs: number; suggestions: number }
}

interface KB {
  id: string
  name: string
  _count: { sources: number; botProfiles: number }
}

const MODES = ['OFF', 'SHADOW', 'SUGGESTIVE', 'AUTOPILOT'] as const
const CHANNELS = ['WEB_CHAT', 'WHATSAPP', 'SMS', 'EMAIL', 'IG_DM', 'FB_MSG'] as const
const MODELS = ['claude-sonnet-4-5', 'claude-haiku-4-5'] as const

const MODE_DESCRIPTIONS: Record<(typeof MODES)[number], string> = {
  OFF: 'no actúa',
  SHADOW: 'genera respuesta, NO la envía (eval)',
  SUGGESTIVE: 'propone draft, humano aprueba',
  AUTOPILOT: 'envía directo · sujeto a confidenceThreshold + keywords',
}

export function BotsPage() {
  const [items, setItems] = useState<BotProfile[]>([])
  const [kbs, setKbs] = useState<KB[]>([])
  const [selected, setSelected] = useState<BotProfile | null>(null)
  const [draft, setDraft] = useState<Partial<BotProfile>>({})
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function load() {
    const [b, k] = await Promise.all([
      api<{ items: BotProfile[] }>('/v1/marketing/bots'),
      api<{ items: KB[] }>('/v1/marketing/bots/knowledge-bases'),
    ])
    setItems(b.items)
    setKbs(k.items)
  }

  useEffect(() => {
    void load()
  }, [])

  function pick(b: BotProfile) {
    setSelected(b)
    setDraft(b)
    setErr(null)
  }

  function newOne() {
    setSelected(null)
    setDraft({
      name: '',
      kind: 'CHAT',
      mode: 'OFF',
      systemPrompt: '',
      channels: ['WEB_CHAT'],
      model: 'claude-sonnet-4-5',
      confidenceThreshold: 0.7,
      responseDelaySeconds: 2,
      fallbackKeywords: ['precio', 'queja', 'urgente', 'hablar con humano'],
      active: true,
    })
    setErr(null)
  }

  async function save() {
    setBusy(true)
    setErr(null)
    try {
      const body = JSON.stringify({
        ...draft,
        confidenceThreshold: Number(draft.confidenceThreshold ?? 0.7),
      })
      if (selected) {
        await api(`/v1/marketing/bots/${selected.id}`, { method: 'PATCH', body })
      } else {
        await api('/v1/marketing/bots', { method: 'POST', body })
      }
      await load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function remove(b: BotProfile) {
    if (!confirm(`¿Eliminar el bot "${b.name}"?`)) return
    await api(`/v1/marketing/bots/${b.id}`, { method: 'DELETE' })
    if (selected?.id === b.id) newOne()
    await load()
  }

  return (
    <div className="p-6 max-w-6xl">
      <header className="mb-4">
        <h1 className="text-lg font-medium tracking-tight flex items-center gap-2">
          <Bot className="size-5 text-blueprint-700" />
          Bots
        </h1>
        <p className="text-xs font-mono text-ink-700/60">
          Conversation AI con Claude · 4 modos · RAG opcional sobre AiKnowledgeBase
        </p>
        <div className="mt-2 inline-flex items-center gap-2 px-2 py-1 rounded text-xs font-mono bg-amber-50 border border-amber-200 text-amber-900">
          F12 prep · runtime (drafts en inbox) llega en Sprint 6
        </div>
      </header>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-4 bp-card overflow-hidden">
          <div className="px-3 py-2 border-b border-ink-900/10 flex items-center justify-between">
            <span className="text-xs font-mono uppercase tracking-wider text-ink-700/60">
              bots
            </span>
            <button onClick={newOne} className="bp-btn" type="button">
              <Plus className="size-3" /> Nuevo
            </button>
          </div>
          <ul>
            {items.map((b) => (
              <li key={b.id}>
                <button
                  type="button"
                  onClick={() => pick(b)}
                  className={[
                    'w-full text-left px-3 py-2 border-b border-ink-900/5 hover:bg-surface-alt',
                    selected?.id === b.id ? 'bg-blueprint-50' : '',
                  ].join(' ')}
                >
                  <div className="text-sm font-medium flex items-center gap-2">
                    {b.name}
                    <span
                      className={[
                        'bp-pill',
                        b.mode === 'AUTOPILOT'
                          ? 'bg-blueprint-600 text-white'
                          : b.mode === 'SUGGESTIVE'
                            ? 'bg-blueprint-50 text-blueprint-700'
                            : b.mode === 'SHADOW'
                              ? 'bg-amber-50 text-amber-700'
                              : 'bg-ink-900/10 text-ink-700/60',
                      ].join(' ')}
                    >
                      {b.mode}
                    </span>
                  </div>
                  <div className="text-[10px] font-mono text-ink-700/60">
                    {b.channels.join(' · ') || '(sin canales)'}
                  </div>
                </button>
              </li>
            ))}
            {items.length === 0 && (
              <li className="px-3 py-6 text-center text-xs text-ink-700/50">sin bots</li>
            )}
          </ul>
          <div className="px-3 py-2 border-t border-ink-900/10 bg-surface-alt/50">
            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60 flex items-center gap-1">
              <Brain className="size-3" /> knowledge bases
            </div>
            <ul className="mt-1 space-y-0.5">
              {kbs.map((kb) => (
                <li key={kb.id} className="text-xs font-mono text-ink-700 flex justify-between">
                  <span>{kb.name}</span>
                  <span className="text-ink-700/50">{kb._count.sources} src</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="col-span-8 bp-card p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
                nombre
              </span>
              <input
                value={draft.name ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
                propósito
              </span>
              <input
                value={draft.purpose ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, purpose: e.target.value }))}
                className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm"
              />
            </label>
          </div>

          <div>
            <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
              modo
            </span>
            <div className="mt-1 flex gap-1">
              {MODES.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setDraft((d) => ({ ...d, mode: m }))}
                  className={[
                    'flex-1 px-2 py-1.5 text-xs border rounded',
                    draft.mode === m
                      ? 'bg-blueprint-600 text-white border-blueprint-700'
                      : 'bg-white border-ink-900/15 text-ink-700',
                  ].join(' ')}
                  title={MODE_DESCRIPTIONS[m]}
                >
                  {m}
                </button>
              ))}
            </div>
            <div className="text-[10px] font-mono text-ink-700/60 mt-1">
              {draft.mode ? MODE_DESCRIPTIONS[draft.mode as (typeof MODES)[number]] : ''}
            </div>
          </div>

          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
              system prompt
            </span>
            <textarea
              value={draft.systemPrompt ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, systemPrompt: e.target.value }))}
              className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-xs font-mono min-h-[200px]"
              spellCheck={false}
            />
          </label>

          <div>
            <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
              canales
            </span>
            <div className="mt-1 flex flex-wrap gap-1">
              {CHANNELS.map((c) => {
                const on = draft.channels?.includes(c)
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() =>
                      setDraft((d) => ({
                        ...d,
                        channels: on
                          ? (d.channels ?? []).filter((x) => x !== c)
                          : [...(d.channels ?? []), c],
                      }))
                    }
                    className={[
                      'px-2 py-0.5 text-xs rounded border',
                      on
                        ? 'bg-blueprint-50 border-blueprint-300 text-blueprint-700'
                        : 'bg-white border-ink-900/15 text-ink-700/60',
                    ].join(' ')}
                  >
                    {c}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
                modelo
              </span>
              <select
                value={draft.model ?? 'claude-sonnet-4-5'}
                onChange={(e) => setDraft((d) => ({ ...d, model: e.target.value }))}
                className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm bg-white"
              >
                {MODELS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
                KB
              </span>
              <select
                value={draft.knowledgeBaseId ?? ''}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, knowledgeBaseId: e.target.value || null }))
                }
                className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm bg-white"
              >
                <option value="">— sin KB —</option>
                {kbs.map((kb) => (
                  <option key={kb.id} value={kb.id}>
                    {kb.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
                confidence
              </span>
              <input
                type="number"
                step="0.01"
                min={0}
                max={1}
                value={draft.confidenceThreshold ?? 0.7}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, confidenceThreshold: Number(e.target.value) }))
                }
                className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm font-mono"
              />
            </label>
          </div>

          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
              fallback keywords (coma)
            </span>
            <input
              value={(draft.fallbackKeywords ?? []).join(', ')}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  fallbackKeywords: e.target.value
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean),
                }))
              }
              className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm font-mono"
            />
          </label>

          {err && (
            <div className="text-xs font-mono text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
              {err}
            </div>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void save()}
              className="bp-btn-primary"
              disabled={busy || !draft.name?.trim() || !draft.systemPrompt?.trim()}
            >
              <Save className="size-3" />
              {selected ? 'Guardar' : 'Crear bot'}
            </button>
            {selected && (
              <button
                type="button"
                onClick={() => void remove(selected)}
                className="bp-btn text-red-700 ml-auto"
              >
                <Trash2 className="size-3" /> Eliminar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
